import React from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import DashboardLayout from "@/components/DashboardLayout";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, Loader2, Camera, RefreshCw } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { logger } from "@/lib/logger";

export default function Storyboards() {
  const { projectId } = useParams();
  const [params, setParams] = useSearchParams();
  const [scenes, setScenes] = React.useState([]);
  const [shots, setShots] = React.useState([]);
  const [activeScene, setActiveScene] = React.useState(params.get("scene"));
  const [loading, setLoading] = React.useState(true);
  const [genShot, setGenShot] = React.useState(null);
  const [busyDirector, setBusyDirector] = React.useState(false);
  const { updateCredits } = useAuth();

  const load = React.useCallback(async () => {
    try {
      const [sc, sh] = await Promise.all([
        api.get(`/projects/${projectId}/scenes`),
        api.get(`/storyboards/project/${projectId}`),
      ]);
      setScenes(sc.data);
      setShots(sh.data);
      setActiveScene((cur) => cur || (sc.data.length > 0 ? sc.data[0].id : null));
    } catch (err) {
      logger.warn("storyboards: load failed", err);
      toast.error("Could not load");
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  React.useEffect(() => { load(); }, [load]);

  const sceneShots = shots.filter((s) => s.scene_id === activeScene).sort((a, b) => a.order - b.order);

  const runDirector = async () => {
    if (!activeScene) return;
    setBusyDirector(true);
    try {
      const { data } = await api.post("/ai/director", { project_id: projectId, scene_id: activeScene });
      updateCredits(data.credits);
      // Apply
      await api.post("/ai/director/apply", { project_id: projectId, scene_id: activeScene, shots: data.plan.shot_plan });
      toast.success("Director plan applied");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Director failed"); }
    finally { setBusyDirector(false); }
  };

  const genImage = async (shotId) => {
    setGenShot(shotId);
    try {
      const { data } = await api.post("/storyboards/generate", { shot_id: shotId });
      updateCredits(data.credits);
      toast.success("Frame generated");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setGenShot(null); }
  };

  return (
    <DashboardLayout>
      <div className="max-w-[1400px] mx-auto px-8 py-10">
        <div className="uppercase text-xs tracking-[0.3em] text-[#D4AF37] mb-2">Storyboard Studio</div>
        <h1 className="font-editorial text-4xl mb-8">Shots</h1>

        {loading ? (
          <div className="text-[#A1A1AA]">Loading…</div>
        ) : scenes.length === 0 ? (
          <div className="border border-[#1E1E1E] bg-[#0F0F10] p-12 text-center">
            <div className="font-editorial text-2xl">No scenes yet.</div>
            <p className="text-sm text-[#A1A1AA] mt-2">Write scene headings in the screenplay to unlock the storyboard.</p>
            <Link to={`/app/projects/${projectId}/script`} className="mt-6 inline-block bg-[#D4AF37] text-[#0A0A0A] px-5 py-2 text-sm font-medium">Open screenplay</Link>
          </div>
        ) : (
          <div className="grid grid-cols-[240px_1fr] gap-6">
            <div className="border border-[#1E1E1E] max-h-[70vh] overflow-y-auto">
              <div className="px-3 py-2.5 text-[10px] uppercase tracking-widest text-[#71717A] border-b border-[#1E1E1E] bg-[#0F0F10]">Scenes</div>
              {scenes.map((s) => (
                <button key={s.id}
                  onClick={() => { setActiveScene(s.id); setParams({ scene: s.id }); }}
                  className={`w-full text-left px-3 py-2.5 text-xs border-b border-[#1E1E1E] hover:bg-[#141414] transition-colors ${activeScene === s.id ? "bg-[#141414] border-l-2 border-l-[#D4AF37] pl-[10px]" : ""}`}
                  data-testid={`storyboard-scene-${s.number}`}>
                  <div className="text-[10px] text-[#71717A]">#{s.number}</div>
                  <div className="text-[#E4E4E7] truncate font-mono-editor">{s.heading}</div>
                </button>
              ))}
            </div>

            <div>
              <div className="flex items-baseline justify-between mb-4">
                <div className="text-sm text-[#A1A1AA]">
                  {sceneShots.length} shot{sceneShots.length === 1 ? "" : "s"}
                </div>
                <button onClick={runDirector} disabled={busyDirector || !activeScene}
                  className="bg-[#D4AF37] text-[#0A0A0A] px-4 py-2 text-xs font-medium inline-flex items-center gap-2 hover:bg-[#F59E0B] disabled:opacity-50 transition-colors"
                  data-testid="storyboard-director-btn">
                  {busyDirector ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
                  {sceneShots.length ? "Regenerate with AI Director" : "Generate shot plan with AI Director"}
                </button>
              </div>

              {sceneShots.length === 0 ? (
                <div className="border border-[#1E1E1E] bg-[#0F0F10] p-10 text-center">
                  <Camera size={28} strokeWidth={1.5} className="mx-auto text-[#D4AF37]" />
                  <div className="font-editorial text-xl mt-3">No shots yet for this scene.</div>
                  <p className="text-sm text-[#A1A1AA] mt-2">Run the AI Director to propose a structured shot plan.</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                  {sceneShots.map((sh) => (
                    <ShotCard key={sh.id} shot={sh} onGen={() => genImage(sh.id)} busy={genShot === sh.id} />
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}

function ShotGenIcon({ busy, hasImage }) {
  if (busy) return <Loader2 size={10} className="animate-spin" />;
  if (hasImage) return <RefreshCw size={10} />;
  return <Sparkles size={10} className="text-[#D4AF37]" />;
}
function ShotGenLabel({ busy, hasImage }) {
  if (busy) return "Generating…";
  return hasImage ? "Regenerate frame" : "Generate frame";
}

function ShotCard({ shot, onGen, busy }) {

  const [dataUrl, setDataUrl] = React.useState(null);
  React.useEffect(() => {
    if (shot.image_asset_id) {
      api.get(`/storyboards/asset/${shot.image_asset_id}`).then((r) => setDataUrl(r.data.data_url)).catch(() => {});
    } else { setDataUrl(null); }
  }, [shot.image_asset_id]);

  return (
    <div className="border border-[#1E1E1E] bg-[#141414]" data-testid={`shot-card-${shot.id}`}>
      <div className="aspect-video bg-[#0A0A0A] relative overflow-hidden border-b border-[#1E1E1E]">
        {dataUrl ? (
          <img src={dataUrl} alt={`Shot ${shot.shot_number}`} className="absolute inset-0 w-full h-full object-cover" />
        ) : (
          <div className="absolute inset-0 flex flex-col items-center justify-center text-[#71717A]">
            <Camera size={22} strokeWidth={1.5} />
            <div className="text-[10px] uppercase tracking-widest mt-1">No frame</div>
          </div>
        )}
        <div className="absolute top-2 left-2 bg-[#0A0A0A]/90 border border-[#27272A] px-2 py-0.5 text-[10px] uppercase tracking-widest">
          Shot {shot.shot_number}
        </div>
      </div>
      <div className="p-4 space-y-2">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-[#D4AF37] flex-wrap">
          <span>{shot.shot_size}</span>
          <span className="text-[#71717A]">/</span>
          <span className="text-[#A1A1AA]">{shot.camera_angle}</span>
          <span className="text-[#71717A]">/</span>
          <span className="text-[#A1A1AA]">{shot.camera_movement}</span>
          <span className="text-[#71717A]">/</span>
          <span className="text-[#A1A1AA]">{shot.lens}</span>
        </div>
        <div className="text-sm text-[#E4E4E7] line-clamp-3 leading-relaxed">{shot.description}</div>
        {shot.director_note && (
          <div className="text-xs text-[#71717A] italic border-l-2 border-[#D4AF37] pl-2">{shot.director_note}</div>
        )}
        <button onClick={onGen} disabled={busy}
          className="w-full mt-2 border border-[#27272A] hover:border-[#D4AF37] text-[10px] uppercase tracking-widest py-2 inline-flex items-center justify-center gap-2 transition-colors"
          data-testid={`shot-gen-${shot.id}`}>
          <ShotGenIcon busy={busy} hasImage={!!dataUrl} />
          <ShotGenLabel busy={busy} hasImage={!!dataUrl} />
        </button>
      </div>
    </div>
  );
}
